import { useTranslation } from 'react-i18next';
import CardData from '../dashboard/cardData';
import { Briefcase, Clipboard, Clock } from '../icons';
import { Flex } from 'antd';
import { Button, Heading, Text } from '../Ant';
import CollapsibleDateGroup from '../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toDateGroupKey,
  useCollapsedDateGroups,
} from '../../hooks/useCollapsedDateGroups';
import {
  DetailsContainer,
  TimerHeaderDetail,
} from '../../assets/styles/components/timerCard.styled';
import { usePage } from '../../context/PageContext';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { LOCAL_STORAGE_NAMES } from '../../constants/constants';
import { ITimer } from '../../interface/timer';
import {
  calculateTimeDuration,
  formatShortDuration,
  formatTime,
  groupTimeSlotsByDate,
} from '../../constants/utilityFunctions';
import dayjs from 'dayjs';
import DescriptionModal from '../descriptionModal/descriptionModal';
import { formatMatterReference } from '../../constants/matter';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';

interface TimerDetailProps {
  timerange: string;
  timelogged: string;
}

const TimerDetails = ({ timerange, timelogged }: TimerDetailProps) => {
  return (
    <DetailsContainer gap={5}>
      <Clock />
      <Flex vertical gap={2}>
        <Heading $fontsize="13px" level={6} $weight={500} $color={'title'} $lineheight="20px">
          {timerange}
        </Heading>
        <Text $fontsize="12px" $weight={400} $color={'subtitle'} $lineheight="18px">
          {timelogged}
        </Text>
      </Flex>
    </DetailsContainer>
  );
};

const TimerCardDetail = () => {
  const { t } = useTranslation();
  const { goBack } = useNavigationStack();
  const { setTitle } = usePage();
  const { timerId } = useParams();
  const { trackPage } = useAnalytics();
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState<boolean>(false);
  const [showMoreContent, setShowMoreContent] = useState<
    { title: string; content: string } | undefined
  >(undefined);
  const existingTimers = localStorage.getItem(LOCAL_STORAGE_NAMES.timer);
  const timers: ITimer[] = existingTimers ? JSON.parse(existingTimers) : [];
  const timer = timers.find((t) => t.id === timerId);
  const { isCollapsed, toggle } = useCollapsedDateGroups(`${DateGroupList.timerSlots}:${timerId}`);

  useEffect(() => {
    trackPage(AnalyticsPage.TIMER_DETAIL);
  }, [trackPage]);

  const handleBack = () => {
    goBack();
  };
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader handleBack={handleBack} title={t('common:timers.timeSlots')} />,
    );
  }, []);

  const handleClose = () => {
    setIsShowMoreModalOpen(false);
  };

  return (
    <Flex vertical className="h-full">
      <TimerHeaderDetail vertical gap={10}>
        <CardData
          type="subject"
          icon={<Clipboard />}
          content={timer?.subject ?? '-'}
          title={t('common:subject')}
          contentclass="fs-13 lineclamp-1"
          moreAction={
            <Button
              data-testid="timer-show-more-button"
              type="text"
              className="text-cta show-more"
              onClick={() => {
                setShowMoreContent({
                  title: t('common:subject'),
                  content: timer?.subject ?? '',
                });
                setIsShowMoreModalOpen(true);
              }}
            >
              {t('common:more')}
            </Button>
          }
        />
        {/* Without a matter there is nothing to name here, and an empty row reads as a value
            the user is missing rather than one they have not picked yet. */}
        {timer?.matter && (
          <CardData
            type="transaction-unit-price-source"
            icon={<Briefcase />}
            title={t('transaction:transactionUnitPriceSource.matter')}
            content={
              timer?.matter?.principalCompany?.name ??
              timer?.matter?.principalPrivatePerson?.fullName ??
              ''
            }
            subcontent={formatMatterReference(timer.matter)}
            contentclass="fs-13 lineclamp-1"
            subcontentclass="lineclamp-1"
          />
        )}
      </TimerHeaderDetail>

      <Flex
        vertical
        className="flex-grow min-h-0 overflow-y-auto scroll-page-gutter pb-gutter gap-gutter"
      >
        {Object.entries(groupTimeSlotsByDate(timer || ({} as ITimer))).map(([date, slots]) => {
          const totalSeconds = calculateTimeDuration(slots);
          const totalDurationFormatted = formatShortDuration(totalSeconds ?? 0);
          const groupKey = toDateGroupKey(slots?.[0]?.start);
          return (
            <CollapsibleDateGroup
              key={groupKey}
              title={date}
              total={totalDurationFormatted}
              collapsed={isCollapsed(groupKey)}
              onToggle={() => toggle(groupKey)}
            >
              {slots?.map((slot) => {
                const start = dayjs(slot.start).format('hh:mm A');
                const end = dayjs(slot.end).format('hh:mm A');
                const formatedDuration = formatTime(slot.duration);
                return (
                  <TimerDetails
                    data-testid="timer-details"
                    key={start}
                    timerange={`${start} - ${end}`}
                    timelogged={`${formatedDuration} h`}
                  />
                );
              })}
            </CollapsibleDateGroup>
          );
        })}
      </Flex>
      {/* Stays mounted through its closing animation, which is when antd hands focus back. */}
      <DescriptionModal
        title={showMoreContent?.title ?? ''}
        description={showMoreContent?.content ?? ''}
        open={isShowMoreModalOpen}
        onClose={handleClose}
      />
    </Flex>
  );
};

export default TimerCardDetail;
